/**
 * The block builder's Fields panel: the version's template variables and a tree of its sample
 * payload, every insertable entry draggable into the editor (FIELD_DRAG_MIME, handled by
 * cdm/fieldInsert.ts's FieldDrop) or clickable to insert at the cursor. Scalars become placeholder
 * chips; arrays become a loop scaffold over the collection with chips for the item's fields.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import type { Editor } from '@tiptap/core'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { VariableKind } from '../../constants'
import type { TemplateVariable } from '../../types'
import { templatesApiService } from '../../services/templatesApiService'
import { useTemplateSamples } from '../../hooks/useTemplateSamples'
import { arrayItemFields, isObject, isScalar } from '../../blocks/sampleFields'
import { insertField, writeFieldPayload, MAX_LOOP_SCAFFOLD_FIELDS, type FieldPayload } from '../../cdm/fieldInsert'

export { arrayItemFields }

/** A loop variable's item fields from its loop-scoped sub-variables ("item.name" → alias item, field name). */
export function loopVariablePayload(loop: TemplateVariable, all: TemplateVariable[]): FieldPayload {
  const subs = all.filter(v => v.loopKey === loop.normalizedKey && v.variableKindLookupId === VariableKind.SCALAR)
  const split = subs.map(v => v.rawName.split('.')).filter(s => s.length >= 2)
  const alias = split[0]?.[0] ?? 'item'
  const itemFields = split.filter(s => s[0] === alias).map(s => s.slice(1).join('.'))
  return { kind: 'list', path: loop.rawName, itemFields: [...new Set(itemFields)].slice(0, MAX_LOOP_SCAFFOLD_FIELDS), alias }
}

interface Props {
  editor: Editor | null
  templateId: string
  templateVersionId: string | null
  onClose?: () => void
}

export default function FieldsPanel({ editor, templateId, templateVersionId, onClose }: Props) {
  const [variables, setVariables] = useState<TemplateVariable[] | null>(null)
  const { samples, sampleId, setSampleId, sampleData } = useTemplateSamples(templateVersionId)

  useEffect(() => {
    if (!templateVersionId) { setVariables([]); return }
    let cancelled = false
    setVariables(null)
    templatesApiService.getVariables(templateVersionId)
      .then(v => { if (!cancelled) setVariables(v) })
      .catch(() => { if (!cancelled) setVariables([]) })
    return () => { cancelled = true }
  }, [templateVersionId])

  const insert = (p: FieldPayload) => { if (editor?.isEditable) insertField(editor, p) }

  const scalars = (variables ?? []).filter(v => v.variableKindLookupId === VariableKind.SCALAR && !v.loopKey)
  const loops = (variables ?? []).filter(v => v.variableKindLookupId === VariableKind.LOOP)
  const conditionals = (variables ?? []).filter(v => v.variableKindLookupId === VariableKind.CONDITIONAL)

  return (
    <aside aria-label="Fields" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: colors.surface, fontSize: fontSize.sm }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: `${spacing.sm} ${spacing[3]}`, borderBottom: `1px solid ${colors.border}` }}>
        <span style={{ fontWeight: 600, color: colors.text, flex: 1 }}>Fields</span>
        {onClose && (
          <button type="button" aria-label="Close fields panel" onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: colors.textSecondary, fontSize: fontSize.base }}>×</button>
        )}
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: `${spacing.sm} ${spacing[3]}` }}>
        <div style={{ color: colors.textMuted, fontSize: fontSize.xs, marginBottom: spacing.sm }}>Drag into the document, or click to insert at the cursor.</div>

        <SectionTitle>Template variables</SectionTitle>
        {variables === null ? <Muted>Loading…</Muted>
          : !templateVersionId ? <Muted>Save the template to list its variables.</Muted>
          : variables.length === 0 ? <Muted>No variables yet.</Muted>
          : (
            <>
              {scalars.length > 0 && <GroupTitle>Scalars</GroupTitle>}
              {scalars.map(v => <FieldItem key={v.normalizedKey} label={v.rawName} payload={{ kind: 'scalar', path: v.rawName }} onInsert={insert} />)}
              {loops.length > 0 && <GroupTitle>Loops</GroupTitle>}
              {loops.map(v => (
                <div key={v.normalizedKey}>
                  <FieldItem label={v.rawName} badge="list" payload={loopVariablePayload(v, variables)} onInsert={insert} />
                  <div style={{ paddingLeft: 14 }}>
                    {variables.filter(s => s.loopKey === v.normalizedKey && s.variableKindLookupId === VariableKind.SCALAR).map(s => (
                      <FieldItem key={s.normalizedKey} label={s.rawName} payload={{ kind: 'scalar', path: s.rawName }} onInsert={insert} />
                    ))}
                  </div>
                </div>
              ))}
              {conditionals.length > 0 && <GroupTitle>Conditionals</GroupTitle>}
              {conditionals.map(v => <div key={v.normalizedKey} style={{ padding: '3px 6px', color: colors.textSecondary, fontFamily: 'monospace' }}>{v.rawName}</div>)}
            </>
          )}

        <SectionTitle>Sample data</SectionTitle>
        {samples === null ? <Muted>Loading…</Muted>
          : samples.length === 0 ? (
            <Muted>
              No sample payloads. <Link to={`/app/templates/${templateId}/preview`} style={{ color: colors.primary }}>Create one on the Preview page</Link>.
            </Muted>
          ) : (
            <>
              {samples.length > 1 && (
                <select
                  aria-label="Sample"
                  value={sampleId ?? ''}
                  onChange={e => setSampleId(e.target.value)}
                  style={{ width: '100%', marginBottom: spacing.sm, padding: '3px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm }}
                >
                  {samples.map(s => <option key={s.sampleId} value={s.sampleId}>{s.name}</option>)}
                </select>
              )}
              {sampleData === null || !isObject(sampleData)
                ? <Muted>This sample is not a JSON object.</Muted>
                : Object.entries(sampleData).map(([k, v]) => <SampleNode key={k} name={k} value={v} path={k} onInsert={insert} />)}
            </>
          )}
      </div>
    </aside>
  )
}

/** One sample-tree entry. `collection` is set below an array: paths there are relative to its item. */
function SampleNode({ name, value, path, collection, onInsert }: {
  name: string; value: unknown; path: string; collection?: string; onInsert: (p: FieldPayload) => void
}) {
  const [open, setOpen] = useState(false)
  if (isScalar(value)) {
    return <FieldItem label={name} hint={value === null ? 'null' : String(value)} payload={collection ? { kind: 'scalar', path, collection } : { kind: 'scalar', path }} onInsert={onInsert} />
  }
  if (Array.isArray(value)) {
    const first = value.find(x => x !== null && x !== undefined)
    if (collection) {
      return <div style={{ padding: '3px 6px', color: colors.textMuted }}>{name} <span style={{ fontSize: fontSize.xs }}>(nested list)</span></div>
    }
    return (
      <div>
        <Row>
          <Toggle open={open} onToggle={() => setOpen(o => !o)} label={name} />
          <FieldItem label={name} badge={`list · ${value.length}`} payload={{ kind: 'list', path, itemFields: arrayItemFields(value) ?? undefined, alias: 'item' }} onInsert={onInsert} />
        </Row>
        {open && isObject(first) && (
          <div style={{ paddingLeft: 14 }}>
            {Object.entries(first).map(([k, v]) => <SampleNode key={k} name={k} value={v} path={k} collection={path} onInsert={onInsert} />)}
          </div>
        )}
      </div>
    )
  }
  const obj = value as Record<string, unknown>
  return (
    <div>
      <Row><Toggle open={open} onToggle={() => setOpen(o => !o)} label={name} /><span style={{ padding: '3px 6px', color: colors.text }}>{name}</span></Row>
      {open && (
        <div style={{ paddingLeft: 14 }}>
          {Object.entries(obj).map(([k, v]) => <SampleNode key={k} name={k} value={v} path={`${path}.${k}`} collection={collection} onInsert={onInsert} />)}
        </div>
      )}
    </div>
  )
}

function FieldItem({ label, hint, badge, payload, onInsert }: {
  label: string; hint?: string; badge?: string; payload: FieldPayload; onInsert: (p: FieldPayload) => void
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      data-field-path={payload.path}
      title={payload.kind === 'list' ? `Insert a loop over ${payload.path}` : `Insert {{${payload.path}}}`}
      onDragStart={e => writeFieldPayload(e.dataTransfer, payload)}
      onClick={() => onInsert(payload)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onInsert(payload) } }}
      style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0, padding: '3px 6px', borderRadius: radius.sm, cursor: 'grab', userSelect: 'none' }}
      onMouseEnter={e => { e.currentTarget.style.background = colors.surfaceMist }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
    >
      <span style={{ fontFamily: 'monospace', color: '#4338ca', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {badge && <span style={{ fontSize: fontSize.xs, color: colors.textSecondary, border: `1px solid ${colors.border}`, borderRadius: radius.full, padding: '0 6px', whiteSpace: 'nowrap' }}>{badge}</span>}
      {hint && <span style={{ fontSize: fontSize.xs, color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hint}</span>}
    </div>
  )
}

function Toggle({ open, onToggle, label }: { open: boolean; onToggle: () => void; label: string }) {
  return (
    <button type="button" aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`} aria-expanded={open} onClick={onToggle}
      style={{ width: 16, border: 'none', background: 'none', cursor: 'pointer', color: colors.textSecondary, padding: 0, fontSize: fontSize.xs }}>
      {open ? '▾' : '▸'}
    </button>
  )
}

const Row = ({ children }: { children: ReactNode }) => <div style={{ display: 'flex', alignItems: 'center' }}>{children}</div>
const Muted = ({ children }: { children: ReactNode }) => <div style={{ color: colors.textMuted, padding: '3px 0' }}>{children}</div>
const SectionTitle = ({ children }: { children: ReactNode }) => (
  <div style={{ fontSize: fontSize.xs, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', margin: `${spacing[3]} 0 ${spacing.xs}` }}>{children}</div>
)
const GroupTitle = ({ children }: { children: ReactNode }) => <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, fontWeight: 600, margin: '6px 0 2px' }}>{children}</div>
